import React, { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "./Header";
import { pageTitles } from "../routes/PageTitle";
import { useLocation, matchPath } from "react-router-dom";
import { useHamburgerMenuInfo } from "../features/hamburgerMenu/useHamburgerMenuInfo";
import { HamburgerMenu } from "./HamburgerMenu";
import { Path } from "../routes/PagePath";
import { useScrollToTop } from "../utils/hooks/useScrollToTop";
import { useBadge } from "../pwa/useBadge";
import VConsole from "vconsole";

if (process.env.VITE_APP_ENVIRONMENT === "development") {
    new VConsole();
}

type Props = {
    children: ReactNode;
};

export const Layout: React.FC<Props> = ({ children }) => {
    const { pathname } = useLocation();
    const { hamburgerMenuInfo } = useHamburgerMenuInfo();
    const refScrollToTop = useScrollToTop();
    useBadge();

    const currentPageTitle = pageTitles.find((title) =>
        matchPath(title.path, pathname)
    );

    const hiddenScrollbarPageList = [Path.memorydetail];
    const isHiddenScrollbar = hiddenScrollbarPageList.some((path) =>
        matchPath(path, pathname)
    );

    // ハンバーガーメニューの開閉状態
    const isMenuOpen = hamburgerMenuInfo.hamburgerMenuIsOpen;

    // コンテンツとメニューを重ねて配置するための共通クラス
    const baseContentClass = "absolute inset-0 overflow-y-auto";

    // メインエリアのスクロール制御: メニュー開閉時にスクロールを無効化して無限スクロールの発火を防ぐ
    const mainScrollClass = isMenuOpen ? "overflow-hidden" : "overflow-y-auto";

    // コンテンツの表示制御: invisibleでDOM上に保持してスクロール位置を維持
    const contentVisibilityClass = isMenuOpen
        ? "invisible pointer-events-none"
        : "";

    // メニューの表示制御: hiddenで完全に非表示
    const menuVisibilityClass = isMenuOpen ? "" : "hidden";

    return (
        <div className="mx-auto flex h-full max-w-2xl flex-col">
            <Helmet>
                <title>
                    {currentPageTitle
                        ? `${currentPageTitle?.title} | ツイオク`
                        : "ツイオク"}
                </title>
            </Helmet>
            <Header />
            <main
                ref={refScrollToTop}
                className={`relative h-full flex-1 pt-16 ${mainScrollClass} ${
                    isHiddenScrollbar ? "hidden-scrollbar" : ""
                }`}
            >
                {/* コンテンツとハンバーガーメニューを同じ位置に重ねて配置 */}
                <div
                    className={`relative h-full ${hamburgerMenuInfo.menuAnimation}`}
                >
                    {/* メインコンテンツ: メニューが開いている時は視覚的に非表示だがDOM上に保持 */}
                    <div
                        className={`${baseContentClass} ${contentVisibilityClass}`}
                    >
                        {children}
                    </div>
                    {/* ハンバーガーメニュー: メニューが閉じている時は完全に非表示 */}
                    <div
                        className={`${baseContentClass} ${menuVisibilityClass}`}
                    >
                        <HamburgerMenu />
                    </div>
                </div>
            </main>
            <div id="image-viewer"></div>
        </div>
    );
};
